<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CreateMcpServerForm from './CreateMcpServerForm.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const router = useRouter();

const isOpen = ref(false);
const dialogRef = ref(null);
const formRef = ref(null);

const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');

const handleSubmit = async () => {
  if (!formRef.value) return;
  const { state, validate } = formRef.value;

  try {
    const isFormValid = await validate();
    if (!isFormValid) return;

    const server = await store.dispatch('wgptMcpServers/create', { ...state });
    useAlert(t('WGPT_MCP_SERVERS.CREATE.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
    emit('close');
    router.push({
      name: 'wgpt_mcp_servers_edit',
      params: { serverId: server.id, tab: 'tools' },
    });
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_MCP_SERVERS.CREATE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleDismiss = () => {
  isOpen.value = false;
  emit('close');
};

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    dialogRef.value?.open();
  });
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_MCP_SERVERS.CREATE.TITLE')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    :confirm-button-label="t('WGPT_MCP_SERVERS.CREATE.FORM.SAVE')"
    :cancel-button-label="t('WGPT_MCP_SERVERS.CREATE.FORM.CANCEL')"
    overflow-y-auto
    @confirm="handleSubmit"
    @close="handleDismiss"
  >
    <CreateMcpServerForm v-if="isOpen" ref="formRef" @submit="handleSubmit" />
  </Dialog>
</template>
